class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.infinite-scroll__button');
    this.status = this.querySelector('[data-infinite-scroll-status]');
    this.sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.isLoading = false;

    if (!this.dataset.nextUrl) return;

    this.classList.add('is-enhanced');

    if (this.button) {
      this.button.addEventListener('click', this.loadNextPage.bind(this));
    }

    if ('IntersectionObserver' in window && this.sentinel && this.dataset.nextUrl) {
      this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
        rootMargin: '900px 0px',
      });
      this.observer.observe(this.sentinel);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  onIntersection(entries) {
    if (entries.some((entry) => entry.isIntersecting)) {
      this.loadNextPage();
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.dataset.nextUrl) return;

    this.setLoading(true);

    try {
      const response = await fetch(this.buildFetchUrl(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.status);

      const html = await response.text();
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const currentList = this.getProductList(document);
      const nextList = this.getProductList(parsedHTML);

      if (!currentList || !nextList) throw new Error('Missing product grid');

      const items = Array.from(nextList.children);
      items.forEach((item) => currentList.appendChild(item));

      this.updatePagination(parsedHTML);
      this.refreshLoadedContent();
    } catch (error) {
      this.setLoading(false);
      this.classList.remove('is-enhanced');
      this.classList.add('is-error');
      this.setStatus(this.dataset.errorText);
    }
  }

  buildFetchUrl() {
    const url = new URL(this.dataset.nextUrl, window.location.href);
    const sectionId = this.getSectionId();

    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }

    return url.toString();
  }

  getSectionId() {
    const productGrid = document.getElementById('product-grid');
    return productGrid ? productGrid.dataset.id : '';
  }

  getProductList(root) {
    const productGrid = root.getElementById('product-grid');
    if (!productGrid) return null;

    return productGrid.matches('ul') ? productGrid : productGrid.querySelector('ul');
  }

  updatePagination(parsedHTML) {
    const nextInfiniteScroll = parsedHTML.querySelector('infinite-scroll');
    const nextPagination = nextInfiniteScroll ? nextInfiniteScroll.querySelector('.pagination') : null;
    const currentPagination = this.querySelector('.pagination');

    if (nextPagination && currentPagination) {
      currentPagination.innerHTML = nextPagination.innerHTML;
    }

    if (nextInfiniteScroll && nextInfiniteScroll.dataset.nextUrl) {
      this.dataset.nextUrl = nextInfiniteScroll.dataset.nextUrl;
      this.setLoading(false);
      this.loadIfSentinelIsNearViewport();
      return;
    }

    this.removeAttribute('data-next-url');
    this.setComplete();
  }

  refreshLoadedContent() {
    document.dispatchEvent(new CustomEvent('infinite-scroll:loaded', { bubbles: true }));

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
  }

  loadIfSentinelIsNearViewport() {
    if (!this.observer || !this.sentinel || !this.dataset.nextUrl) return;

    const sentinelTop = this.sentinel.getBoundingClientRect().top;

    if (sentinelTop < window.innerHeight + 900) {
      window.setTimeout(() => this.loadNextPage(), 0);
    }
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.classList.toggle('is-loading', isLoading);

    if (this.button) {
      this.button.disabled = isLoading;
      this.button.setAttribute('aria-busy', isLoading.toString());
    }

    this.querySelectorAll('.loading__spinner').forEach((spinner) => {
      spinner.classList.toggle('hidden', !isLoading);
    });

    this.setStatus(isLoading ? this.dataset.loadingText : '');
  }

  setComplete() {
    if (this.observer) this.observer.disconnect();
    if (this.button) this.button.closest('.infinite-scroll__button-wrapper').remove();
    this.classList.remove('is-loading');
    this.classList.add('is-complete');
    this.setStatus(this.dataset.completeText);
  }

  setStatus(message) {
    if (this.status) {
      this.status.textContent = message || '';
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
